import { useContext } from 'react';
import { Edit } from '@mui/icons-material';
import { Dialog, DialogTitle, IconButton, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import {
  EstateResultItemServiceStatus,
  EstateResultItemType,
  EstateResultSource,
  useEstateResultItem_GetEstateResultItemByIdLazyQuery,
} from 'queries';
import { X } from 'ts-react-feather-icons';

import { NotificationSeverity, showAlert, showToast } from 'utils/notification-manager';
import useIsImpersonating from 'utils/useIsImpersonating';

import {
  EstateResultItemState,
  EstateResultsPageContext,
} from '../context/estate-results-page-context';

type Props = {
  id: string | undefined;
  showModal: boolean;
  setShowModal: (value: boolean) => void;
  onExitClick: () => void;
  title: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  items: [any];
};

const useStyles = makeStyles((theme) => ({
  root: { textAlign: 'center' },
  actions: {
    display: 'flex',
    justifyContent: 'center',
    margin: 'auto',
  },
  closeButton: {
    position: 'absolute',
    right: theme.spacing(1),
    top: theme.spacing(1),
    color: theme.palette.grey[500],
  },
  editButton: {
    position: 'absolute',
    right: theme.spacing(6),
    top: theme.spacing(1),
    color: theme.palette.grey[500],
  },
  logo: {
    height: '60px',
    width: '60px',
    margin: 'auto',
    marginTop: '75px',
  },
  title: {
    fontWeight: 600,
  },
  description: {
    width: '50%',
    marginBottom: '30px',
    fontWeight: 600,
  },
  buttonText: {
    textAlign: 'center',
    display: 'flex',
    fontWeight: 600,
  },
  media: {
    objectFit: 'scale-down',
  },
  dataList: {
    columns: 2,
    textAlign: 'start',
  },
  exportBtn: {
    background: theme.palette.secondary.main,
  },
}));

const LearnMoreModal = ({ id, showModal, setShowModal, onExitClick, title, items }: Props) => {
  const classes = useStyles();
  const { setContext } = useContext(EstateResultsPageContext);
  const isImpersonating = useIsImpersonating();

  const [getEstateResultItemById] = useEstateResultItem_GetEstateResultItemByIdLazyQuery({
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    onCompleted: (data) => {
      const estateResultItemData = data.data;
      if (!estateResultItemData) {
        return;
      }
      if (estateResultItemData.serviceStatus === EstateResultItemServiceStatus.Purged) {
        showToast({
          message: 'Unable to edit a purged record.',
          severity: NotificationSeverity.WARNING,
        });
        onExitClick();
      }
      if (estateResultItemData.source === EstateResultSource.Equifax) {
        showToast({
          message: 'Unable to edit an Equifax record.',
          severity: NotificationSeverity.WARNING,
        });
        onExitClick();
      }
      if (estateResultItemData.type === EstateResultItemType.Source) {
        showToast({
          message: 'Unable to edit a source record.',
          severity: NotificationSeverity.WARNING,
        });
        onExitClick();
      }
      if (
        estateResultItemData.serviceStatus !== EstateResultItemServiceStatus.Purged &&
        estateResultItemData.source !== EstateResultSource.Equifax
      ) {
        const newContextValues: EstateResultItemState = {
          isEditingEstateResultItem: true,
          estateResultItemIdToEdit: id || null,
          setContext: setContext,
        };
        setContext(newContextValues);
        setShowModal(false);
      }
    },
    onError: (e) => {
      showAlert({
        message: `Unable to get estate result item by id. ${e}`,
        severity: NotificationSeverity.ERROR,
      });
      console.log(e);
    },
  });

  const onEditClick = async () => {
    if (!id) {
      return;
    }
    await getEstateResultItemById({
      variables: { id: id },
    });
  };

  return (
    <Dialog
      data-testid="learnMoreModal"
      fullWidth={true}
      maxWidth="md"
      open={showModal}
      transitionDuration={{ enter: 300, exit: 0 }}
      onClose={() => {
        setShowModal(false);
      }}
      className={classes.root}
    >
      <img src="/trustate-logo.svg" className={classes.logo} alt="trustate" />
      <DialogTitle>
        <Typography className={classes.title} variant="h4">
          {title}
        </Typography>
        <IconButton
          aria-label="close"
          className={classes.closeButton}
          onClick={onExitClick}
          size="large"
        >
          <X />
        </IconButton>
        {isImpersonating && (
          <IconButton
            aria-label="edit"
            className={classes.editButton}
            onClick={onEditClick}
            size="large"
          >
            <Edit />
          </IconButton>
        )}
      </DialogTitle>
      <Typography className={classes.description} variant="body1" style={{ width: 'auto' }}>
        <div>
          <ul className={classes.dataList}>
            {// eslint-disable-next-line @typescript-eslint/no-explicit-any
            items?.map((item: any) => {
              return (
                <li key={item.field}>
                  {item.field}: {item.value}
                </li>
              );
            })}
          </ul>
        </div>
      </Typography>
    </Dialog>
  );
};
export default LearnMoreModal;
